import React, { useState, useEffect, useRef } from 'react';
import { ColumnMapping, MetadataReviewData, ValidationWarning } from '../types/columnMapping';
import { columnMappingApi } from '../services/columnMappingApi';
import { api } from '../services/api';
import './MetadataEditor.css';

interface MetadataEditorProps {
  reviewData: MetadataReviewData;
  contextId: string | null | undefined;
  sessionId: string;
  onConfirm: () => void;
  onCancel: () => void;
  currentPhase?: number;
  hasGeneratedScript?: boolean;
  hasExecuted?: boolean;
}

export const MetadataEditor: React.FC<MetadataEditorProps> = ({
  reviewData,
  contextId,
  sessionId,
  onConfirm,
  onCancel,
  currentPhase = 1,
  hasGeneratedScript = false,
  hasExecuted = false
}) => {
  const [mappings, setMappings] = useState<ColumnMapping[]>(() => {
    // Ensure all mappings have enrichment fields for display
    console.log('[MetadataEditor] Initial mappings from reviewData:', reviewData.mappings);
    return reviewData.mappings.map(m => {
      // Log what enrichment data we're getting
      console.log(`[MetadataEditor] Mapping for ${m.original}:`, {
        semantic_type: m.semantic_type,
        business_context: m.business_context,
        data_format: m.data_format,
        validation_rules: m.validation_rules,
        detected_type: m.detected_type,
        enrichment_confidence: m.enrichment_confidence
      });
      return {
        ...m,
        // Add default values if not present to ensure UI displays correctly
        detected_type: m.detected_type || 'text',
        enrichment_confidence: m.enrichment_confidence || 'medium'
      };
    });
  });
  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [enrichingColumns, setEnrichingColumns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [selectedAdditionalColumns, setSelectedAdditionalColumns] = useState<string[]>([]);
  const [showAIReasoning, setShowAIReasoning] = useState(false); // Collapsed by default
  
  // Function to dynamically position tooltips to avoid container overflow
  const handleTooltipPosition = (tooltip: HTMLElement) => {
    // Make tooltip visible first to get accurate measurements
    tooltip.classList.add('visible');
    
    // Get bounding rectangles
    const rect = tooltip.getBoundingClientRect();
    const containerRect = tooltip.closest('.mapping-rows-container')?.getBoundingClientRect();
    
    if (containerRect) {
      // Check if tooltip goes above the container
      if (rect.top < containerRect.top) {
        tooltip.classList.add('tooltip-below');
        tooltip.classList.remove('tooltip-above');
      } 
      // Check if tooltip goes below the container
      else if (rect.bottom > containerRect.bottom) {
        tooltip.classList.add('tooltip-above');
        tooltip.classList.remove('tooltip-below');
      }
      // Default position works fine
      else {
        tooltip.classList.remove('tooltip-below', 'tooltip-above');
      }
    }
  };
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set()); // For bulk operations
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set()); // For showing metadata details
  const [editingField, setEditingField] = useState<{row: number, field: string} | null>(null);
  
  // Track user modifications
  const [originalMappings] = useState<ColumnMapping[]>(reviewData.mappings);
  const [columnsAdded, setColumnsAdded] = useState<string[]>([]);
  const [columnsRemoved, setColumnsRemoved] = useState<string[]>([]);
  const [metadataChanges, setMetadataChanges] = useState<Array<{column: string, old_metadata: string, new_metadata: string}>>([]);
  
  // Historical hints state
  const [historicalHints, setHistoricalHints] = useState<any>(null);
  const [showHints, setShowHints] = useState(false);
  const [loadingHints, setLoadingHints] = useState(false);

  useEffect(() => {
    // Load suggestions for all columns
    loadSuggestions();
    // Initialize available columns (those not already mapped)
    initializeAvailableColumns();
    // Load historical hints
    loadHistoricalHints();
    
    // Cleanup function to clear any pending timeouts
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [reviewData.domain]); // Removed data_type which no longer exists

  // Validate contextId after hooks
  if (!contextId || contextId === '') {
    console.error('MetadataEditor: Invalid contextId provided');
    return (
      <div className="metadata-editor-error">
        <p>Invalid workflow context. Please restart your workflow.</p>
        <button onClick={onCancel} className="btn btn-secondary">Close</button>
      </div>
    );
  }

  const initializeAvailableColumns = () => {
    // Get all columns that are not currently mapped
    const mappedColumns = new Set(mappings.map(m => m.original));
    const allCols = reviewData.all_columns || [];
    const optional = reviewData.optional_columns_list || [];
    const skipped = reviewData.skipped_columns_list || [];
    
    // Combine optional and skipped columns that aren't already mapped
    const available = [
      ...optional.filter(col => !mappedColumns.has(col)),
      ...skipped.filter(col => {
        const colName = typeof col === 'string' ? col : col.column;
        return !mappedColumns.has(colName);
      }).map(col => typeof col === 'string' ? col : col.column)
    ];
    
    // Add any columns from all_columns that aren't mapped and aren't in our lists
    allCols.forEach(col => {
      if (!mappedColumns.has(col) && !available.includes(col)) {
        available.push(col);
      }
    });
    
    setAvailableColumns(available);
  };

  const loadSuggestions = async () => {
    setLoadingSuggestions(true);
    setError(null);
    
    try {
      const suggestionsMap: Record<string, string[]> = {};
      
      // Use reviewData.mappings directly to avoid stale state issues
      const mappingsToLoad = reviewData.mappings;
      
      // Load suggestions in parallel for better performance
      const suggestionPromises = mappingsToLoad.map(async (mapping) => {
        try {
          const response = await columnMappingApi.getMetadataSuggestions(
            reviewData.domain,
            null, // data_type removed from system
            mapping.original
          );
          
          // The API returns {column: suggestions[]} format
          const suggestions = response.data[mapping.original] || [];
          if (suggestions.length > 0) {
            return { column: mapping.original, suggestions: suggestions };
          }
          return null;
        } catch (err) {
          console.error(`Failed to load suggestions for ${mapping.original}:`, err);
          return null;
        }
      });
      
      const results = await Promise.all(suggestionPromises);
      
      results.forEach(result => {
        if (result) {
          suggestionsMap[result.column] = result.suggestions;
        }
      });
      
      setSuggestions(suggestionsMap);
      console.log('[MetadataEditor] Initial suggestions loaded:', suggestionsMap); // Debug log
    } catch (error) {
      console.error('Failed to load suggestions:', error);
      setError('Failed to load metadata suggestions. You can still proceed with manual mapping.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleMappingChange = (index: number, newMetadata: string) => {
    const updatedMappings = [...mappings];
    const oldMetadata = updatedMappings[index].metadata;
    const columnName = updatedMappings[index].original;
    
    updatedMappings[index] = {
      ...updatedMappings[index],
      metadata: newMetadata,
      recognized: false // Mark as manually changed
    };
    setMappings(updatedMappings);
    
    // Don't hide dropdown when typing
    if (showDropdown === columnName) {
      // Keep dropdown open while typing
    } else {
      setShowDropdown(null);
    }
    
    // Track metadata change
    if (oldMetadata !== newMetadata) {
      const existingChange = metadataChanges.find(c => c.column === columnName);
      if (existingChange) {
        // Update existing change
        setMetadataChanges(metadataChanges.map(c => 
          c.column === columnName 
            ? { ...c, new_metadata: newMetadata }
            : c
        ));
      } else {
        // Add new change
        setMetadataChanges([...metadataChanges, {
          column: columnName,
          old_metadata: oldMetadata,
          new_metadata: newMetadata
        }]);
      }
    }
  };

  const handleRequirementToggle = (index: number) => {
    const updatedMappings = [...mappings];
    const mapping = updatedMappings[index];
    
    // Toggle between required (true/undefined) and optional (false)
    updatedMappings[index] = {
      ...mapping,
      is_required: mapping.is_required === false ? true : false
    };
    
    setMappings(updatedMappings);
  };

  const toggleRowExpansion = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const getStalenessColor = (staleness?: string) => {
    switch (staleness) {
      case 'recent': return '#4CAF50';
      case 'moderate': return '#FFC107';
      case 'stale': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const handleFieldEdit = (index: number, field: string, value: string) => {
    const updatedMappings = [...mappings];
    if (field === 'semantic_type') {
      updatedMappings[index].semantic_type = value;
    } else if (field === 'business_context') {
      updatedMappings[index].business_context = value;
    }
    setMappings(updatedMappings);
    setEditingField(null);
  };

  const handleSelectRow = (index: number) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(index)) {
      newSelectedRows.delete(index);
    } else {
      newSelectedRows.add(index);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleSelectAllRows = () => {
    if (selectedRows.size === mappings.length) {
      // Deselect all
      setSelectedRows(new Set());
    } else {
      // Select all
      const allIndices = new Set(mappings.map((_, index) => index));
      setSelectedRows(allIndices);
    }
  };

  const handleBulkRequirementChange = (isRequired: boolean) => {
    const updatedMappings = [...mappings];
    selectedRows.forEach(index => {
      updatedMappings[index] = {
        ...updatedMappings[index],
        is_required: isRequired
      };
    });
    setMappings(updatedMappings);
    // Clear selection after bulk operation
    setSelectedRows(new Set());
  };
  
  const handleBulkAcceptSuggestions = () => {
    if (!reviewData.validation_warnings) return;
    
    const updatedMappings = [...mappings];
    let changesApplied = 0;
    
    // Apply suggestions only for selected rows
    selectedRows.forEach(index => {
      const mapping = mappings[index];
      const warnings = reviewData.validation_warnings![mapping.original];
      
      if (warnings && warnings.length > 0) {
        const warningWithSuggestion = warnings
          .filter(w => w.suggestion)
          .sort((a, b) => {
            const severityOrder = { 'high': 0, 'medium': 1, 'low': 2, 'info': 3 };
            return (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
          })[0];
        
        if (warningWithSuggestion && warningWithSuggestion.suggestion) {
          if (mapping.metadata !== warningWithSuggestion.suggestion) {
            const oldMetadata = mapping.metadata;
            updatedMappings[index] = {
              ...mapping,
              metadata: warningWithSuggestion.suggestion,
              reason: `Applied suggestion from validation (was: ${oldMetadata})`
            };
            changesApplied++;
            
            setMetadataChanges(prev => [
              ...prev,
              {
                column: mapping.original,
                old_metadata: oldMetadata,
                new_metadata: warningWithSuggestion.suggestion!
              }
            ]);
          }
        }
      }
    });
    
    if (changesApplied > 0) {
      setMappings(updatedMappings);
      setSelectedRows(new Set());
    }
  };
  
  // Count suggestions available for selected rows
  const getSelectedSuggestionsCount = () => {
    if (!reviewData.validation_warnings) return 0;
    
    let count = 0;
    selectedRows.forEach(index => {
      const mapping = mappings[index];
      const warnings = reviewData.validation_warnings![mapping.original];
      if (warnings) {
        count += warnings.filter(w => w.suggestion).length > 0 ? 1 : 0;
      }
    });
    return count;
  };

  const handleSelectAllAvailableColumns = () => {
    if (selectedAdditionalColumns.length === availableColumns.length) {
      // Deselect all
      setSelectedAdditionalColumns([]);
    } else {
      // Select all
      setSelectedAdditionalColumns([...availableColumns]);
    }
  };

  const handleRemoveColumn = (index: number) => {
    const removedColumn = mappings[index];
    const updatedMappings = mappings.filter((_, i) => i !== index);
    setMappings(updatedMappings);
    // Add removed column back to available columns
    setAvailableColumns([...availableColumns, removedColumn.original]);
    
    // Clear selection if this row was selected
    if (selectedRows.has(index)) {
      const newSelectedRows = new Set(selectedRows);
      newSelectedRows.delete(index);
      // Adjust indices for rows after the removed one
      const adjustedRows = new Set<number>();
      newSelectedRows.forEach(rowIndex => {
        if (rowIndex > index) {
          adjustedRows.add(rowIndex - 1);
        } else {
          adjustedRows.add(rowIndex);
        }
      });
      setSelectedRows(adjustedRows);
    }
    
    // Track column removal
    const wasOriginal = originalMappings.some(m => m.original === removedColumn.original);
    if (wasOriginal) {
      setColumnsRemoved([...columnsRemoved, removedColumn.original]);
    } else {
      // Remove from added list if it was added in this session
      setColumnsAdded(columnsAdded.filter(c => c !== removedColumn.original));
    }
  };

  const handleAddColumns = async () => {
    if (selectedAdditionalColumns.length === 0) return;
    
    setEnrichingColumns(true);
    setError(null);
    
    try {
      // Call enrichment API for the selected columns
      const enrichmentResponse = await fetch(`/api/workflow/context/${contextId}/enrich-columns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          columns: selectedAdditionalColumns,
          file_ids: reviewData.file_id ? [reviewData.file_id] : [],
          domain: reviewData.domain
        })
      });
      
      let enrichedColumnsData: ColumnMapping[] = [];
      
      if (enrichmentResponse.ok) {
        const enrichmentData = await enrichmentResponse.json();
        
        if (enrichmentData.success && enrichmentData.enriched_columns) {
          // Use enriched metadata
          enrichedColumnsData = enrichmentData.enriched_columns.map((col: any) => ({
            original: col.original,
            metadata: col.metadata,
            reason: col.reason || 'AI-enriched for analysis',
            selection_reason: col.selection_reason || 'User manually added - AI enriched',
            recognized: false,
            is_required: true,
            semantic_type: col.semantic_type,
            business_context: col.business_context,
            data_format: col.data_format,
            validation_rules: col.validation_rules,
            enrichment_confidence: col.enrichment_confidence,
            detected_type: col.detected_type
          }));
          
          // Handle validation warnings if any
          if (enrichmentData.validation_warnings && Object.keys(enrichmentData.validation_warnings).length > 0) {
            // Merge warnings with existing validation warnings
            const updatedReviewData = {
              ...reviewData,
              validation_warnings: {
                ...reviewData.validation_warnings,
                ...enrichmentData.validation_warnings
              }
            };
            // Note: We can't directly update reviewData as it's a prop
            // The warnings will be shown inline with the columns
          }
        } else {
          // Enrichment failed but API returned 200
          throw new Error(enrichmentData.error || 'Enrichment failed');
        }
      } else {
        // API call failed
        throw new Error(`Enrichment API failed with status ${enrichmentResponse.status}`);
      }
      
      // Add enriched columns to mappings
      setMappings([...mappings, ...enrichedColumnsData]);
      
      // Track columns added
      const newlyAdded = selectedAdditionalColumns.filter(col => 
        !originalMappings.some(m => m.original === col)
      );
      setColumnsAdded([...columnsAdded, ...newlyAdded]);
      
      // Remove added columns from available
      const addedSet = new Set(selectedAdditionalColumns);
      setAvailableColumns(availableColumns.filter(col => !addedSet.has(col)));
      
      // Reset selection
      setSelectedAdditionalColumns([]);
      setShowColumnSelector(false);
      
      // Load suggestions for new columns
      loadSuggestionsForColumns(selectedAdditionalColumns);
      
    } catch (err: any) {
      console.error('Failed to enrich columns:', err);
      
      // Fallback to basic transformation
      const fallbackMappings: ColumnMapping[] = selectedAdditionalColumns.map(col => ({
        original: col,
        metadata: col.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        reason: 'Manually added (enrichment unavailable)',
        selection_reason: 'User override - column added manually',
        recognized: false,
        is_required: true
      }));
      
      setMappings([...mappings, ...fallbackMappings]);
      
      // Track columns added
      const newlyAdded = selectedAdditionalColumns.filter(col => 
        !originalMappings.some(m => m.original === col)
      );
      setColumnsAdded([...columnsAdded, ...newlyAdded]);
      
      // Remove added columns from available
      const addedSet = new Set(selectedAdditionalColumns);
      setAvailableColumns(availableColumns.filter(col => !addedSet.has(col)));
      
      // Reset selection
      setSelectedAdditionalColumns([]);
      setShowColumnSelector(false);
      
      // Show error message
      setError(`Column enrichment failed: ${err.message}. Using basic metadata transformation.`);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
      
      // Load suggestions for new columns
      loadSuggestionsForColumns(selectedAdditionalColumns);
    } finally {
      setEnrichingColumns(false);
    }
  };

  const loadSuggestionsForColumns = async (columns: string[]) => {
    try {
      const suggestionsMap = { ...suggestions };
      
      const suggestionPromises = columns.map(async (column) => {
        try {
          const response = await columnMappingApi.getMetadataSuggestions(
            reviewData.domain,
            null, // data_type removed from system
            column
          );
          
          // The API returns {column: suggestions[]} format
          const suggestions = response.data[column] || [];
          if (suggestions.length > 0) {
            return { column, suggestions: suggestions };
          }
          return null;
        } catch (err) {
          console.error(`Failed to load suggestions for ${column}:`, err);
          return null;
        }
      });
      
      const results = await Promise.all(suggestionPromises);
      
      results.forEach(result => {
        if (result) {
          suggestionsMap[result.column] = result.suggestions;
        }
      });
      
      setSuggestions(suggestionsMap);
    } catch (error) {
      console.error('Failed to load suggestions for new columns:', error);
    }
  };

  const toggleColumnSelection = (column: string) => {
    if (selectedAdditionalColumns.includes(column)) {
      setSelectedAdditionalColumns(selectedAdditionalColumns.filter(c => c !== column));
    } else {
      setSelectedAdditionalColumns([...selectedAdditionalColumns, column]);
    }
  };

  const loadHistoricalHints = async () => {
    if (!reviewData.domain) return;
    
    setLoadingHints(true);
    try {
      const response = await columnMappingApi.getHistoricalHints(
        reviewData.domain,
        reviewData.data_type,
        sessionId
      );
      
      if (response.data) {
        setHistoricalHints(response.data);
      }
    } catch (error) {
      console.error('Failed to load historical hints:', error);
    } finally {
      setLoadingHints(false);
    }
  };

  const applyHint = (type: 'add' | 'remove' | 'metadata', column: string, newMetadata?: string) => {
    switch (type) {
      case 'remove':
        // Remove the column from mappings
        const indexToRemove = mappings.findIndex(m => m.original === column);
        if (indexToRemove !== -1) {
          handleRemoveColumn(indexToRemove);
        }
        break;
        
      case 'add':
        // Add the column to selected additional columns for adding
        if (!selectedAdditionalColumns.includes(column)) {
          setSelectedAdditionalColumns([column]);
          // Trigger add columns
          setTimeout(() => {
            const newMapping: ColumnMapping = {
              original: column,
              metadata: column.toLowerCase().replace(/[^a-z0-9]/g, '_'),
              reason: 'Added based on historical preference',
              selection_reason: 'Previously added by user in similar workflows',
              recognized: false,
              is_required: true // Default to required when user adds columns
            };
            
            setMappings([...mappings, newMapping]);
            
            // Track column addition
            const wasOriginal = originalMappings.some(m => m.original === column);
            if (!wasOriginal) {
              setColumnsAdded([...columnsAdded, column]);
            }
            
            // Remove from available columns
            setAvailableColumns(availableColumns.filter(col => col !== column));
            setSelectedAdditionalColumns([]);
            
            // Load suggestions for the new column
            loadSuggestionsForColumns([column]);
          }, 0);
        }
        break;
        
      case 'metadata':
        // Change the metadata for a column
        if (newMetadata) {
          const index = mappings.findIndex(m => m.original === column);
          if (index !== -1) {
            handleMappingChange(index, newMetadata);
          }
        }
        break;
    }
  };

  const applyAllHints = () => {
    if (!historicalHints) return;
    
    // Apply all removals
    if (historicalHints.previously_removed) {
      historicalHints.previously_removed.forEach((hint: any) => {
        const index = mappings.findIndex(m => m.original === hint.column);
        if (index !== -1) {
          handleRemoveColumn(index);
        }
      });
    }
    
    // Apply all additions
    if (historicalHints.previously_added) {
      const columnsToAdd = historicalHints.previously_added
        .filter((hint: any) => availableColumns.includes(hint.column))
        .map((hint: any) => hint.column);
      
      if (columnsToAdd.length > 0) {
        const newMappings: ColumnMapping[] = columnsToAdd.map((col: string) => ({
          original: col,
          metadata: col.toLowerCase().replace(/[^a-z0-9]/g, '_'),
          reason: 'Added based on historical preference',
          selection_reason: 'Previously added by user in similar workflows',
          recognized: false,
          is_required: true // Default to required when user adds columns
        }));
        
        setMappings([...mappings, ...newMappings]);
        
        // Track additions
        const newlyAdded = columnsToAdd.filter((col: string) => 
          !originalMappings.some(m => m.original === col)
        );
        setColumnsAdded([...columnsAdded, ...newlyAdded]);
        
        // Update available columns
        const addedSet = new Set(columnsToAdd);
        setAvailableColumns(availableColumns.filter(col => !addedSet.has(col)));
        
        // Load suggestions
        loadSuggestionsForColumns(columnsToAdd);
      }
    }
    
    // Apply all metadata preferences
    if (historicalHints.metadata_preferences) {
      Object.entries(historicalHints.metadata_preferences).forEach(([col, pref]: [string, any]) => {
        const index = mappings.findIndex(m => m.original === col);
        if (index !== -1 && mappings[index].metadata !== pref.preferred) {
          handleMappingChange(index, pref.preferred);
        }
      });
    }
    
    // Hide hints after applying
    setShowHints(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate that at least one column is selected
      if (mappings.length === 0) {
        setError('Please select at least one column to continue with the workflow.');
        setLoading(false);
        return;
      }
      
      // Validate all mappings have metadata
      const invalidMappings = mappings.filter(m => !m.metadata || m.metadata.trim() === '');
      if (invalidMappings.length > 0) {
        setError(`Please provide metadata names for: ${invalidMappings.map(m => m.original).join(', ')}`);
        setLoading(false);
        return;
      }
      
      // Track user modifications if any
      if (columnsAdded.length > 0 || columnsRemoved.length > 0 || metadataChanges.length > 0) {
        try {
          await columnMappingApi.trackUserModifications({
            session_id: sessionId,
            domain: reviewData.domain,
            data_type: reviewData.data_type,
            user_prompt: '',  // TODO: Get from context
            columns_added: columnsAdded,
            columns_removed: columnsRemoved,
            metadata_changes: metadataChanges
          });
        } catch (trackError) {
          // Log but don't fail the confirmation
          console.error('Failed to track user modifications:', trackError);
        }
      }
      
      // Save mappings - ensure all required fields are present
      const mappingsWithReason = mappings.map(m => ({
        ...m,
        reason: m.reason || 'User confirmed mapping'
      }));
      
      // Create a mapping of original column names to metadata names
      const columnMappingsDict: Record<string, string> = {};
      const requiredColumns: string[] = [];
      const optionalColumns: string[] = [];
      
      mappingsWithReason.forEach(m => {
        columnMappingsDict[m.original] = m.metadata;
        // Check if this is a required or optional column based on is_required flag
        // undefined or true means required, false means optional
        if (m.is_required !== false) {
          requiredColumns.push(m.original);
        } else {
          optionalColumns.push(m.original);
        }
      });
      
      // First, save the mappings to the column mapping database for future recognition
      try {
        const confirmResponse = await api.post('/column-mapping/confirm-metadata', {
            file_id: reviewData.file_id || '',
            file_ids: [reviewData.file_id], // API expects array but we only have single file_id
            context_id: contextId,
            mappings: mappingsWithReason.map(m => ({
              original: m.original,
              metadata: m.metadata
            }))
        });
        
        if (!confirmResponse.status || confirmResponse.status >= 400) {
          console.error('Failed to save column mappings to database, but continuing with workflow');
        }
      } catch (confirmError) {
        // Log but don't fail the workflow
        console.error('Failed to save column mappings to database:', confirmError);
      }
      
      // Use the workflow API to set column mappings
      const response = await api.put(`/workflow/context/${contextId}/mappings`, {
        mappings: columnMappingsDict,
        selection_metadata: {
          total_columns: reviewData.total_columns,
          selected_columns: mappings.length,
          optional_columns_count: reviewData.optional_columns || 0,
          skipped_columns: (reviewData.total_columns || 0) - mappings.length - (reviewData.optional_columns || 0),
          user_modified: columnsAdded.length > 0 || columnsRemoved.length > 0 || metadataChanges.length > 0,
          required_columns: requiredColumns,
          optional_columns: optionalColumns
        },
        triggeredFromPhase: currentPhase
      });
      
      if (!response.status || response.status >= 400) {
        const errorData = response.data;
        throw new Error(errorData?.detail || 'Failed to save mappings');
      }
      
      onConfirm();
    } catch (error: any) {
      console.error('Failed to confirm mappings:', error);
      console.error('Error response data:', error.response?.data);
      
      // Handle different error formats
      let errorMessage = 'Failed to save column mappings. Please try again.';
      
      if (error.response?.data?.detail) {
        // Check if detail is a string or an array of validation errors
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors come as an array
          console.error('Validation errors:', error.response.data.detail);
          errorMessage = error.response.data.detail
            .map((err: any) => {
              // Pydantic errors have loc (location) and msg (message)
              if (err.loc && err.msg) {
                return `${err.loc.join('.')}: ${err.msg}`;
              }
              return err.msg || err.message || 'Validation error';
            })
            .join('; ');
        } else if (typeof error.response.data.detail === 'object') {
          // Single validation error object
          errorMessage = error.response.data.detail.msg || 
                        error.response.data.detail.message || 
                        JSON.stringify(error.response.data.detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getMappingStatus = (mapping: ColumnMapping) => {
    if (mapping.recognized && mapping.usage_count && mapping.usage_count > 0) {
      return { 
        icon: '✓', 
        className: 'status-recognized', 
        title: `Previously used (${mapping.usage_count} time${mapping.usage_count > 1 ? 's' : ''})` 
      };
    } else {
      return { 
        icon: '●', 
        className: 'status-new', 
        title: 'New standardized name' 
      };
    }
  };

  const getFilteredSuggestions = (columnName: string, currentValue: string): string[] => {
    const allSuggestions = suggestions[columnName] || [];
    console.log(`[MetadataEditor] Suggestions for ${columnName}:`, allSuggestions, 'Total loaded:', Object.keys(suggestions).length); // Enhanced debug log
    
    if (!currentValue) return allSuggestions.slice(0, 10); // Show max 10 when empty
    
    const lowerValue = currentValue.toLowerCase();
    const filtered = allSuggestions.filter(s => {
      // Extract just the name part for filtering (remove "used X times" part)
      const match = s.match(/^(.*?)\s*(?:\(used \d+ times?\))?.*$/);
      const suggestionName = match ? match[1].trim() : s;
      return suggestionName.toLowerCase().includes(lowerValue);
    });
    
    // Sort by relevance
    return filtered.sort((a, b) => {
      // Extract names for comparison
      const matchA = a.match(/^(.*?)\s*(?:\(used \d+ times?\))?.*$/);
      const matchB = b.match(/^(.*?)\s*(?:\(used \d+ times?\))?.*$/);
      const nameA = matchA ? matchA[1].trim() : a;
      const nameB = matchB ? matchB[1].trim() : b;
      
      const aStarts = nameA.toLowerCase().startsWith(lowerValue);
      const bStarts = nameB.toLowerCase().startsWith(lowerValue);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return nameA.localeCompare(nameB);
    }).slice(0, 10); // Limit to 10 suggestions
  };

  const checkForDuplicates = (value: string, currentIndex: number): string | null => {
    if (!value) return null;
    
    const normalized = value.toLowerCase().trim();
    const duplicate = mappings.find((m, idx) => 
      idx !== currentIndex && 
      m.metadata && 
      m.metadata.toLowerCase().trim() === normalized
    );
    
    if (duplicate) {
      return `Already used for "${duplicate.original}"`;
    }
    return null;
  };
  
  // Calculate warning summary
  const getWarningSummary = () => {
    if (!reviewData.validation_warnings || typeof reviewData.validation_warnings !== 'object') return null;
    
    let highCount = 0;
    let mediumCount = 0;
    let totalWarnings = 0;
    let suggestionsAvailable = 0;
    
    // Safely iterate over validation warnings
    const warningsObj = reviewData.validation_warnings || {};
    Object.values(warningsObj).forEach((warnings: any) => {
      if (!Array.isArray(warnings)) return;
      (warnings as ValidationWarning[]).forEach(warning => {
        totalWarnings++;
        if (warning.severity === 'high') highCount++;
        else if (warning.severity === 'medium') mediumCount++;
        if (warning.suggestion) suggestionsAvailable++;
      });
    });
    
    if (totalWarnings === 0) return null;
    
    return { total: totalWarnings, high: highCount, medium: mediumCount, suggestions: suggestionsAvailable };
  };
  
  const warningSummary = getWarningSummary();
  
  // Handle accepting all suggestions
  const handleAcceptAllSuggestions = () => {
    if (!reviewData.validation_warnings) return;
    
    const updatedMappings = [...mappings];
    let changesApplied = 0;
    
    // Go through each mapping and apply suggestions
    updatedMappings.forEach((mapping, index) => {
      const warnings = reviewData.validation_warnings![mapping.original];
      if (warnings && warnings.length > 0) {
        // Find the highest severity warning with a suggestion
        const warningWithSuggestion = warnings
          .filter(w => w.suggestion)
          .sort((a, b) => {
            // Sort by severity (high > medium > low > info)
            const severityOrder = { 'high': 0, 'medium': 1, 'low': 2, 'info': 3 };
            return (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
          })[0];
        
        if (warningWithSuggestion && warningWithSuggestion.suggestion) {
          // Track metadata change
          if (mapping.metadata !== warningWithSuggestion.suggestion) {
            const oldMetadata = mapping.metadata;
            updatedMappings[index] = {
              ...mapping,
              metadata: warningWithSuggestion.suggestion,
              reason: `Applied suggestion from validation (was: ${oldMetadata})`
            };
            changesApplied++;
            
            // Track the change
            setMetadataChanges(prev => [
              ...prev,
              {
                column: mapping.original,
                old_metadata: oldMetadata,
                new_metadata: warningWithSuggestion.suggestion!
              }
            ]);
          }
        }
      }
    });
    
    if (changesApplied > 0) {
      setMappings(updatedMappings);
      // Note: We should clear warnings from state, not mutate props
      // The parent component should handle clearing warnings via a callback
      // For now, we just update the mappings and let the UI reflect the changes
    }
  };

  return (
    <div className="metadata-editor-overlay">
      <div className="metadata-editor">
        <div className="metadata-header">
          <div className="metadata-header-content">
            <h3>Review Column Mappings</h3>
            <span className="metadata-subtitle">
              Domain: <strong>{reviewData.domain.toUpperCase()}</strong>
            </span>
          </div>
          {reviewData.total_columns && (
            <div className="metadata-stats">
              <span className="stat-item">
                <span className="stat-value">{reviewData.total_columns}</span>
                <span className="stat-label">TOTAL</span>
              </span>
              <span className="stat-separator">•</span>
              <span className="stat-item">
                <span className="stat-value required">{reviewData.required_columns || 0}</span>
                <span className="stat-label">REQUIRED</span>
              </span>
              <span className="stat-separator">•</span>
              <span className="stat-item">
                <span className="stat-value optional">{reviewData.optional_columns || 0}</span>
                <span className="stat-label">OPTIONAL</span>
              </span>
              <span className="stat-separator">•</span>
              <span className="stat-item">
                <span className="stat-value skipped">
                  {reviewData.skipped_columns_list ? reviewData.skipped_columns_list.length : 
                   ((reviewData.total_columns || 0) - (reviewData.required_columns || 0) - (reviewData.optional_columns || 0))}
                </span>
                <span className="stat-label">SKIPPED</span>
              </span>
              <button 
                className="view-all-toggle"
                onClick={() => setShowAllColumns(!showAllColumns)}
                style={{marginLeft: '12px'}}
              >
                {showAllColumns ? 'Hide' : 'View'} Skipped Columns
              </button>
            </div>
          )}
        </div>
        
        <div className="metadata-subheader">
          <div className="mapping-legend">
            <span><span className="status-indicator status-recognized">✓</span> Previously used</span>
            <span><span className="status-indicator status-new">●</span> New standardized name</span>
          </div>
          {reviewData.selection_summary && (
            <button 
              className="ai-reasoning-toggle"
              onClick={() => setShowAIReasoning(!showAIReasoning)}
            >
              {showAIReasoning ? '▼' : '▶'} Assertion's Reasoning
            </button>
          )}
        </div>
        
        {showAIReasoning && reviewData.selection_summary && (
          <div className="ai-reasoning-content">
            <p className="selection-summary">
              {reviewData.selection_summary}
            </p>
          </div>
        )}

        <div className="metadata-content">
          {/* Validation warning summary */}
          {warningSummary && warningSummary.high > 0 && (
            <div className="metadata-warning validation-summary">
              <span className="warning-icon">⚠️</span>
              <div className="warning-content">
                <div>
                  <strong>Metadata Name Conflicts Detected</strong>
                  <p>
                    Found {warningSummary.high} high-severity {warningSummary.high === 1 ? 'warning' : 'warnings'} about duplicate or similar metadata names.
                    Review the highlighted columns below and consider using the suggested alternatives.
                  </p>
                </div>
                {warningSummary.suggestions > 0 && (
                  <button 
                    className="btn btn-primary btn-sm accept-all-suggestions"
                    onClick={handleAcceptAllSuggestions}
                    title={`Apply ${warningSummary.suggestions} suggested ${warningSummary.suggestions === 1 ? 'improvement' : 'improvements'}`}
                  >
                    Accept All Suggestions ({warningSummary.suggestions})
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Phase-aware warnings */}
          {currentPhase >= 2 && hasGeneratedScript && (
            <div className="metadata-warning">
              <span className="warning-icon">⚠️</span>
              <div>
                <strong>Script Regeneration Required</strong>
                <p>Changing columns will regenerate your script. Any manual edits will be lost.</p>
              </div>
            </div>
          )}
          
          {hasExecuted && (
            <div className="metadata-warning">
              <span className="warning-icon">⚠️</span>
              <div>
                <strong>Previous Results Will Be Cleared</strong>
                <p>Changing columns will clear your previous execution results.</p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="metadata-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
          
          {loadingSuggestions && (
            <div className="metadata-loading">
              <span className="loading-spinner"></span>
              Loading suggestions...
            </div>
          )}
          
          <div className="metadata-controls">
            <div className="metadata-instructions-container">
              <p className="metadata-instructions">
                Review and adjust how columns will be interpreted for your {reviewData.domain} workflow:
              </p>
              <button
                className="guide-icon-btn"
                onMouseEnter={(e) => {
                  const tooltip = e.currentTarget.querySelector('.guide-tooltip');
                  if (tooltip) {
                    (tooltip as HTMLElement).style.display = 'block';
                  }
                }}
                onMouseLeave={(e) => {
                  const tooltip = e.currentTarget.querySelector('.guide-tooltip');
                  if (tooltip) {
                    (tooltip as HTMLElement).style.display = 'none';
                  }
                }}
                type="button"
              >
                ℹ️
                <div className="guide-tooltip" style={{ display: 'none' }}>
                  <strong>Column Classification Guide:</strong>
                  <div className="guide-content">
                    <div className="guide-item">
                      <span className="required-label">Required columns</span>
                      <span>MUST be used in the script generation - Assertion will ensure they are included</span>
                    </div>
                    <div className="guide-item">
                      <span className="optional-label">Optional columns</span>
                      <span>may be used by Assertion for additional context or analysis if helpful</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
            <div className="metadata-controls-right">
              {historicalHints && (
                historicalHints.previously_removed?.length > 0 || 
                historicalHints.previously_added?.length > 0 || 
                Object.keys(historicalHints.metadata_preferences || {}).length > 0
              ) && (
                <button 
                  className="hints-toggle"
                  onClick={() => setShowHints(!showHints)}
                  disabled={loadingHints}
                >
                  💡 Previous adjustments {showHints ? '▼' : '▶'}
                </button>
              )}
              <button 
                className="btn-add-columns"
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                disabled={availableColumns.length === 0}
              >
                + Add More Columns
              </button>
            </div>
          </div>

          {showColumnSelector && (
            <div className="column-selector">
              <div className="selector-header">
                <h4>Select Additional Columns to Map</h4>
                <button 
                  className="btn-text"
                  onClick={handleSelectAllAvailableColumns}
                  disabled={enrichingColumns}
                >
                  {selectedAdditionalColumns.length === availableColumns.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {enrichingColumns && (
                <div className="enrichment-loading-message">
                  <span className="loading-spinner">⏳</span>
                  <span>Enriching columns with AI semantic understanding...</span>
                </div>
              )}
              <div className="available-columns-grid">
                {availableColumns.map(col => (
                  <label key={col} className="column-checkbox">
                    <input 
                      type="checkbox"
                      checked={selectedAdditionalColumns.includes(col)}
                      onChange={() => toggleColumnSelection(col)}
                      disabled={enrichingColumns}
                    />
                    <span>{col}</span>
                  </label>
                ))}
              </div>
              <div className="selector-actions">
                <button 
                  className="btn btn-outline"
                  onClick={() => {
                    setShowColumnSelector(false);
                    setSelectedAdditionalColumns([]);
                  }}
                  disabled={enrichingColumns}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleAddColumns}
                  disabled={selectedAdditionalColumns.length === 0 || enrichingColumns}
                >
                  {enrichingColumns 
                    ? 'Enriching with AI...' 
                    : `Add ${selectedAdditionalColumns.length} Column${selectedAdditionalColumns.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          )}
          
          {showHints && historicalHints && (
            <div className="historical-hints">
              {historicalHints.previously_removed?.length > 0 && (
                <div className="hints-section">
                  <h5>Previously Removed</h5>
                  {historicalHints.previously_removed.map((hint: any) => 
                    mappings.find(m => m.original === hint.column) && (
                      <div key={hint.column} className="hint-item">
                        <span className="hint-text">
                          "{hint.column}" ({hint.count} time{hint.count !== 1 ? 's' : ''})
                        </span>
                        <button 
                          className="hint-action"
                          onClick={() => applyHint('remove', hint.column)}
                        >
                          Remove again
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
              
              {historicalHints.previously_added?.length > 0 && (
                <div className="hints-section">
                  <h5>Previously Added</h5>
                  {historicalHints.previously_added.map((hint: any) => 
                    availableColumns.includes(hint.column) && (
                      <div key={hint.column} className="hint-item">
                        <span className="hint-text">
                          "{hint.column}" ({hint.count} time{hint.count !== 1 ? 's' : ''})
                        </span>
                        <button 
                          className="hint-action"
                          onClick={() => applyHint('add', hint.column)}
                        >
                          Add again
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
              
              {Object.keys(historicalHints.metadata_preferences || {}).length > 0 && (
                <div className="hints-section">
                  <h5>Preferred Mappings</h5>
                  {Object.entries(historicalHints.metadata_preferences || {}).map(([col, pref]: [string, any]) => {
                    const currentMapping = mappings.find(m => m.original === col);
                    return currentMapping && currentMapping.metadata !== pref.preferred && (
                      <div key={col} className="hint-item">
                        <span className="hint-text">
                          "{col}" → "{pref.preferred}" ({pref.count} time{pref.count !== 1 ? 's' : ''})
                        </span>
                        <button 
                          className="hint-action"
                          onClick={() => applyHint('metadata', col, pref.preferred)}
                        >
                          Apply
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {(historicalHints.previously_removed?.length > 0 || 
                historicalHints.previously_added?.length > 0 || 
                Object.keys(historicalHints.metadata_preferences || {}).length > 0) && (
                <div className="hints-actions">
                  <button 
                    className="btn btn-outline"
                    onClick={applyAllHints}
                  >
                    Apply all suggestions
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Bulk actions bar */}
          {selectedRows.size > 0 && (
            <div className="bulk-actions-bar">
              <span className="bulk-selection-info">
                {selectedRows.size} column{selectedRows.size !== 1 ? 's' : ''} selected
              </span>
              <div className="bulk-actions">
                <button 
                  className="btn btn-sm btn-outline"
                  onClick={() => handleBulkRequirementChange(true)}
                >
                  Mark as Required
                </button>
                <button 
                  className="btn btn-sm btn-outline"
                  onClick={() => handleBulkRequirementChange(false)}
                >
                  Mark as Optional
                </button>
                {getSelectedSuggestionsCount() > 0 && (
                  <button 
                    className="btn btn-sm btn-primary"
                    onClick={handleBulkAcceptSuggestions}
                  >
                    Accept Suggestions ({getSelectedSuggestionsCount()})
                  </button>
                )}
                <button 
                  className="btn btn-sm btn-text"
                  onClick={() => setSelectedRows(new Set())}
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          <div className="mapping-table">
            {/* Wrapper for both header and scrollable content */}
            <div className="mapping-table-wrapper">
              {/* Header row */}
              <div className="mapping-header">
                <div className="col-checkbox">
                  <input 
                    type="checkbox"
                    checked={mappings.length > 0 && selectedRows.size === mappings.length}
                    onChange={handleSelectAllRows}
                    title="Select all columns"
                  />
                </div>
                <div className="col-original">Original Column</div>
                <div className="col-arrow"></div>
                <div className="col-metadata">Standardized Name</div>
                <div className="col-requirement">Required</div>
                <div className="col-confidence">Status</div>
                <div className="col-actions"></div>
              </div>

              {/* Content rows in scrollable container */}
              <div className="mapping-rows-container">
              {mappings.map((mapping, index) => {
                const status = getMappingStatus(mapping);
                const filteredSuggestions = getFilteredSuggestions(mapping.original, mapping.metadata);
                const duplicateWarning = checkForDuplicates(mapping.metadata, index);
                const validationWarnings = reviewData.validation_warnings?.[mapping.original] || [];
                
                return (
                  <React.Fragment key={index}>
                    <div className={`mapping-row ${selectedRows.has(index) ? 'selected' : ''}`}>
                    <div className="col-checkbox">
                      <input 
                        type="checkbox"
                        checked={selectedRows.has(index)}
                        onChange={() => handleSelectRow(index)}
                      />
                    </div>
                    <div className="col-original">
                    <div className="column-header">
                      <button 
                        className="expand-btn"
                        onClick={() => toggleRowExpansion(index)}
                        title="Show metadata details"
                      >
                        {expandedRows.has(index) ? '▼' : '▶'}
                      </button>
                      <span className="original-name">
                        {mapping.original}
                      </span>
                      {/* Show detected data type next to name */}
                      {mapping.detected_type && (
                        <div className="detected-type-wrapper">
                          <span 
                            className="detected-type"
                            onMouseEnter={(e) => {
                              const target = e.currentTarget;
                              const tooltip = target.nextElementSibling as HTMLElement;
                              if (tooltip) {
                                handleTooltipPosition(tooltip);
                              }
                            }}
                            onMouseLeave={(e) => {
                              const target = e.currentTarget;
                              const tooltip = target.nextElementSibling;
                              if (tooltip) {
                                tooltip.classList.remove('visible');
                              }
                            }}
                          >
                            ({mapping.detected_type})
                          </span>
                          <div className="detected-type-tooltip">
                            <strong>Detected Data Type: {mapping.detected_type.charAt(0).toUpperCase() + mapping.detected_type.slice(1)}</strong>
                            <div className="type-explanation">
                              {mapping.detected_type === 'date' && (
                                <>
                                  <p>Column contains date/datetime values.</p>
                                  <div className="type-details">
                                    <span className="detail-label">Common formats:</span>
                                    <ul>
                                      <li>2024-01-15</li>
                                      <li>01/15/2024</li>
                                      <li>Jan 15, 2024</li>
                                      <li>2024-01-15 14:30:00</li>
                                    </ul>
                                  </div>
                                </>
                              )}
                              {mapping.detected_type === 'numeric' && (
                                <>
                                  <p>Column contains numerical values (integers or decimals).</p>
                                  <div className="type-details">
                                    <span className="detail-label">Examples:</span>
                                    <ul>
                                      <li>1,234.56</li>
                                      <li>-500.00</li>
                                      <li>0.75</li>
                                      <li>1000000</li>
                                    </ul>
                                  </div>
                                </>
                              )}
                              {mapping.detected_type === 'text' && (
                                <>
                                  <p>Column contains text/string values.</p>
                                  <div className="type-details">
                                    <span className="detail-label">Examples:</span>
                                    <ul>
                                      <li>Customer names</li>
                                      <li>Descriptions</li>
                                      <li>Categories</li>
                                      <li>Free-form text</li>
                                    </ul>
                                  </div>
                                </>
                              )}
                              {mapping.detected_type === 'categorical' && (
                                <>
                                  <p>Column contains repeating categorical values.</p>
                                  <div className="type-details">
                                    <span className="detail-label">Characteristics:</span>
                                    <ul>
                                      <li>Limited set of values</li>
                                      <li>Repeated across rows</li>
                                      <li>Often used for grouping</li>
                                      <li>E.g., Status, Type, Region</li>
                                    </ul>
                                  </div>
                                </>
                              )}
                              {mapping.detected_type === 'boolean' && (
                                <>
                                  <p>Column contains true/false or yes/no values.</p>
                                  <div className="type-details">
                                    <span className="detail-label">Common values:</span>
                                    <ul>
                                      <li>True / False</li>
                                      <li>Yes / No</li>
                                      <li>1 / 0</li>
                                      <li>Y / N</li>
                                    </ul>
                                  </div>
                                </>
                              )}
                              {mapping.detected_type === 'mixed' && (
                                <>
                                  <p>Column contains mixed data types.</p>
                                  <div className="type-details">
                                    <span className="detail-label">Note:</span>
                                    <p className="type-warning">This column has inconsistent data types. Review carefully to ensure proper handling.</p>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="type-note">
                              This helps Assertion understand how to process the data.
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Usage badge if applicable */}
                      {mapping.usage_count !== undefined && (
                        <div className="usage-indicator-wrapper">
                          <span 
                            className={`usage-badge ${mapping.usage_count === 0 ? 'unused' : 'used'}`}
                            onMouseEnter={(e) => {
                              const target = e.currentTarget;
                              const tooltip = target.nextElementSibling as HTMLElement;
                              if (tooltip) {
                                handleTooltipPosition(tooltip);
                              }
                            }}
                            onMouseLeave={(e) => {
                              const target = e.currentTarget;
                              const tooltip = target.nextElementSibling;
                              if (tooltip) {
                                tooltip.classList.remove('visible');
                              }
                            }}
                          >
                            {mapping.usage_count === 0 ? '0' : `✓ ${mapping.usage_count}x`}
                          </span>
                          <div className="usage-tooltip">
                            <strong>Usage History</strong>
                            <div className="usage-explanation">
                              {mapping.usage_count === 0 ? (
                                <p>This column mapping has never been used before. Assertion will learn from your usage to improve future suggestions.</p>
                              ) : (
                                <p>This standardized name has been used {mapping.usage_count} time{mapping.usage_count !== 1 ? 's' : ''} for similar columns. This helps maintain consistency across your workflows.</p>
                              )}
                            </div>
                            <div className="usage-note">
                              Tip: Consistent naming improves automation accuracy.
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Confidence indicator with detailed tooltip */}
                      {mapping.enrichment_confidence && (
                        <div className="confidence-indicator-wrapper">
                          <span 
                            className="confidence-indicator"
                            onMouseEnter={(e) => {
                              const target = e.currentTarget;
                              const tooltip = target.nextElementSibling as HTMLElement;
                              if (tooltip) {
                                handleTooltipPosition(tooltip);
                              }
                            }}
                            onMouseLeave={(e) => {
                              const target = e.currentTarget;
                              const tooltip = target.nextElementSibling;
                              if (tooltip) {
                                tooltip.classList.remove('visible');
                              }
                            }}
                          >
                            {mapping.enrichment_confidence === 'high' ? '●' : mapping.enrichment_confidence === 'medium' ? '◐' : '○'}
                          </span>
                          <div className="confidence-tooltip">
                            <strong>AI Confidence: {mapping.enrichment_confidence.charAt(0).toUpperCase() + mapping.enrichment_confidence.slice(1)}</strong>
                            <div className="confidence-explanation">
                              {mapping.enrichment_confidence === 'high' && (
                                <>
                                  <span className="confidence-icon">● High</span>
                                  <p>Clear patterns detected. Column purpose is obvious from name and data (e.g., "Invoice_Date" with date values).</p>
                                </>
                              )}
                              {mapping.enrichment_confidence === 'medium' && (
                                <>
                                  <span className="confidence-icon">◐ Medium</span>
                                  <p>Some ambiguity exists. Column likely correct but multiple interpretations possible (e.g., "Amount" could be various types).</p>
                                </>
                              )}
                              {mapping.enrichment_confidence === 'low' && (
                                <>
                                  <span className="confidence-icon">○ Low</span>
                                  <p>Unclear purpose. Generic name or inconsistent data makes interpretation difficult (e.g., "Field1", "Data").</p>
                                </>
                              )}
                            </div>
                            <div className="confidence-note">
                              Note: Review all mappings regardless of confidence level.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-arrow">→</div>
                  
                  <div className="col-metadata">
                    <div className="metadata-input-container">
                      <input
                        type="text"
                        className={`metadata-input ${mapping.is_required !== false ? 'is-required' : 'is-optional'} ${duplicateWarning ? 'has-warning' : ''}`}
                        value={mapping.metadata}
                        onChange={(e) => handleMappingChange(index, e.target.value)}
                        onFocus={() => {
                          console.log(`[MetadataEditor] Focus on ${mapping.original}`);
                          // Clear any pending blur timeout
                          if (dropdownTimeoutRef.current) {
                            clearTimeout(dropdownTimeoutRef.current);
                            dropdownTimeoutRef.current = null;
                          }
                          setShowDropdown(mapping.original);
                        }}
                        onBlur={() => {
                          console.log(`[MetadataEditor] Blur on ${mapping.original}`);
                          // Use ref to store timeout so we can cancel it if needed
                          dropdownTimeoutRef.current = setTimeout(() => {
                            setShowDropdown(null);
                            dropdownTimeoutRef.current = null;
                          }, 300);
                        }}
                        placeholder="Enter standardized name"
                      />
                      
                      {duplicateWarning && (
                        <div className="duplicate-warning">
                          ⚠️ {duplicateWarning}
                        </div>
                      )}
                      
                      {/* Validation warnings indicator with tooltip */}
                      {validationWarnings.length > 0 && (
                        (() => {
                          // Categorize warnings by severity
                          const warningsBySeverity = {
                            high: validationWarnings.filter((w: ValidationWarning) => w.severity === 'high'),
                            medium: validationWarnings.filter((w: ValidationWarning) => w.severity === 'medium'),
                            low: validationWarnings.filter((w: ValidationWarning) => w.severity === 'low'),
                            info: validationWarnings.filter((w: ValidationWarning) => w.severity === 'info')
                          };
                          
                          // Determine the highest severity for the indicator
                          let indicatorIcon = 'ℹ️';
                          let indicatorClass = 'info';
                          if (warningsBySeverity.high.length > 0) {
                            indicatorIcon = '🔴';  // Red circle for critical
                            indicatorClass = 'high';
                          } else if (warningsBySeverity.medium.length > 0) {
                            indicatorIcon = '🟡';  // Yellow circle for warnings
                            indicatorClass = 'medium';
                          } else if (warningsBySeverity.low.length > 0) {
                            indicatorIcon = '🟢';  // Green circle for suggestions
                            indicatorClass = 'low';
                          } else if (warningsBySeverity.info.length > 0) {
                            indicatorIcon = 'ℹ️';  // Info square for information
                            indicatorClass = 'info';
                          }
                          
                          return (
                            <div className="validation-indicator-wrapper">
                              <span 
                                className={`validation-indicator validation-${indicatorClass}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const target = e.currentTarget;
                                  const tooltip = target.nextElementSibling as HTMLElement;
                                  if (tooltip) {
                                    if (!tooltip.classList.contains('visible')) {
                                      handleTooltipPosition(tooltip);
                                    } else {
                                      tooltip.classList.remove('visible');
                                    }
                                  }
                                }}
                                title={`${validationWarnings.length} validation ${validationWarnings.length === 1 ? 'message' : 'messages'}`}
                              >
                                {indicatorIcon}
                              </span>
                              <div className="validation-tooltip">
                                <strong>Metadata Validation & Suggestions</strong>
                                
                                <div className="validation-explanation">
                                  <span className="info-label">What these indicators mean:</span>
                                  <div className="indicator-definitions">
                                    <div className="definition-item">
                                      <span className="def-icon">🔴</span>
                                      <span className="def-text"><strong>Critical:</strong> Must be fixed for accuracy</span>
                                    </div>
                                    <div className="definition-item">
                                      <span className="def-icon">🟡</span>
                                      <span className="def-text"><strong>Warning:</strong> Should be addressed</span>
                                    </div>
                                    <div className="definition-item">
                                      <span className="def-icon">🟢</span>
                                      <span className="def-text"><strong>Suggestion:</strong> Optional improvements</span>
                                    </div>
                                    <div className="definition-item">
                                      <span className="def-icon">ℹ️</span>
                                      <span className="def-text"><strong>Info:</strong> Helpful context</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="validation-content">
                                  {warningsBySeverity.high.length > 0 && (
                                    <div className="severity-group">
                                      <div className="severity-header">🔴 Critical Issues ({warningsBySeverity.high.length})</div>
                                      {warningsBySeverity.high.map((warning: ValidationWarning, idx: number) => (
                                        <div key={`high-${idx}`} className="warning-item">
                                          <span className="warning-bullet">•</span>
                                          <span className="warning-text">{warning.message}</span>
                                          {warning.suggestion && (
                                            <button 
                                              className="btn-use-suggestion-inline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMappingChange(index, warning.suggestion!);
                                                e.currentTarget.closest('.validation-tooltip')?.classList.remove('visible');
                                              }}
                                            >
                                              Use: {warning.suggestion}
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {warningsBySeverity.medium.length > 0 && (
                                    <div className="severity-group">
                                      <div className="severity-header">🟡 Warnings ({warningsBySeverity.medium.length})</div>
                                      {warningsBySeverity.medium.map((warning: ValidationWarning, idx: number) => (
                                        <div key={`med-${idx}`} className="warning-item">
                                          <span className="warning-bullet">•</span>
                                          <span className="warning-text">{warning.message}</span>
                                          {warning.suggestion && (
                                            <button 
                                              className="btn-use-suggestion-inline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMappingChange(index, warning.suggestion!);
                                                e.currentTarget.closest('.validation-tooltip')?.classList.remove('visible');
                                              }}
                                            >
                                              Use: {warning.suggestion}
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {warningsBySeverity.low.length > 0 && (
                                    <div className="severity-group">
                                      <div className="severity-header">🟢 Suggestions ({warningsBySeverity.low.length})</div>
                                      {warningsBySeverity.low.map((warning: ValidationWarning, idx: number) => (
                                        <div key={`low-${idx}`} className="warning-item">
                                          <span className="warning-bullet">•</span>
                                          <span className="warning-text">{warning.message}</span>
                                          {warning.suggestion && (
                                            <button 
                                              className="btn-use-suggestion-inline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMappingChange(index, warning.suggestion!);
                                                e.currentTarget.closest('.validation-tooltip')?.classList.remove('visible');
                                              }}
                                            >
                                              Use: {warning.suggestion}
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {warningsBySeverity.info.length > 0 && (
                                    <div className="severity-group">
                                      <div className="severity-header">ℹ️ Information ({warningsBySeverity.info.length})</div>
                                      {warningsBySeverity.info.map((warning: ValidationWarning, idx: number) => (
                                        <div key={`info-${idx}`} className="warning-item">
                                          <span className="warning-bullet">•</span>
                                          <span className="warning-text">{warning.message}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                
                                <div className="validation-tip">
                                  Click suggestions to apply • Click icon to dismiss
                                </div>
                              </div>
                            </div>
                          );
                        })()
                      )}
                      
                      {showDropdown === mapping.original && (
                        <div className="suggestions-menu">
                          {filteredSuggestions.length > 0 ? (
                            <>
                              <div className="suggestions-header">Suggestions:</div>
                              {filteredSuggestions.map((suggestion, i) => {
                                // Parse the suggestion to extract just the name
                                const match = suggestion.match(/^(.*?)\s*(?:\(used (\d+) times?\))?(.*)$/);
                                const suggestionName = match ? match[1].trim() : suggestion;
                                
                                return (
                                  <div
                                    key={i}
                                    className={`suggestion-item ${suggestionName === mapping.metadata ? 'selected' : ''}`}
                                    onMouseDown={(e) => {
                                      e.preventDefault(); // Prevent blur from firing
                                      handleMappingChange(index, suggestionName);
                                      setShowDropdown(null); // Hide dropdown after selection
                                    }}
                                  >
                                    {suggestionName}
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div className="no-suggestions">
                              {mapping.metadata ? 
                                "No matching suggestions" : 
                                "No suggestions yet - type a custom name"}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-requirement">
                    <button
                      className={`requirement-toggle ${mapping.is_required !== false ? 'required' : 'optional'}`}
                      onClick={() => handleRequirementToggle(index)}
                      title={mapping.is_required !== false ? 'Click to make optional' : 'Click to make required'}
                    >
                      {mapping.is_required !== false ? 'Required' : 'Optional'}
                    </button>
                  </div>
                  
                  <div className="col-confidence">
                    <span 
                      className={`status-indicator ${status.className}`}
                      title={status.title}
                    >
                      {status.icon}
                    </span>
                  </div>
                  
                  <div className="col-actions">
                    <button 
                      className="btn-remove"
                      onClick={() => handleRemoveColumn(index)}
                      title="Remove this column from mapping"
                    >
                      ×
                    </button>
                    {/* Data quality indicators */}
                    {mapping.data_characteristics && (
                      <div className="data-indicators">
                        {mapping.data_characteristics?.null_percentage !== undefined && mapping.data_characteristics.null_percentage > 0 && (
                          <span className="data-indicator" title={`${mapping.data_characteristics.null_percentage}% nulls`}>
                            ⚠️ {mapping.data_characteristics.null_percentage}% null
                          </span>
                        )}
                        {mapping.data_characteristics?.anomalies && mapping.data_characteristics.anomalies.length > 0 && (
                          <span className="data-indicator anomaly" title={mapping.data_characteristics.anomalies.join(', ')}>
                            ⚠️ Issues
                          </span>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                  
                  {/* Details are shown only in the expanded view below */}
                
                {/* Expanded metadata details */}
                {expandedRows.has(index) && (
                  <div className="metadata-details">
                    <div className="detail-grid">
                      {/* Semantic Type */}
                      <div className="detail-item">
                        <label>Semantic Type:</label>
                        <span className="semantic-display">
                          {mapping.semantic_type || 'Not identified'}
                        </span>
                      </div>
                      
                      {/* Business Context */}
                      <div className="detail-item full-width">
                        <label>Business Context:</label>
                        {editingField?.row === index && editingField?.field === 'business_context' ? (
                          <textarea
                            defaultValue={mapping.business_context || ''}
                            onBlur={(e) => handleFieldEdit(index, 'business_context', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setEditingField(null);
                              }
                            }}
                            autoFocus
                            rows={2}
                          />
                        ) : (
                          <span 
                            className="editable-field"
                            onClick={() => setEditingField({row: index, field: 'business_context'})}
                          >
                            {mapping.business_context || 'Not provided by AI enrichment'}
                          </span>
                        )}
                      </div>
                      
                      {/* Detected Data Type (Editable) */}
                      <div className="detail-item">
                        <label>Data Type:</label>
                        {editingField?.row === index && editingField?.field === 'detected_type' ? (
                          <select
                            defaultValue={mapping.detected_type || 'text'}
                            onBlur={(e) => handleFieldEdit(index, 'detected_type', e.target.value)}
                            onChange={(e) => handleFieldEdit(index, 'detected_type', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setEditingField(null);
                              }
                            }}
                            autoFocus
                            className="type-select"
                          >
                            <option value="text">Text</option>
                            <option value="numeric">Numeric</option>
                            <option value="integer">Integer</option>
                            <option value="decimal">Decimal</option>
                            <option value="monetary">Monetary Value</option>
                            <option value="percentage">Percentage</option>
                            <option value="date">Date</option>
                            <option value="datetime">Date/Time</option>
                            <option value="boolean">Boolean (Yes/No)</option>
                            <option value="categorical">Category</option>
                            <option value="identifier">ID/Code</option>
                            <option value="email">Email</option>
                            <option value="phone">Phone</option>
                            <option value="url">URL</option>
                            <option value="json">JSON</option>
                            <option value="mixed">Mixed</option>
                          </select>
                        ) : (
                          <span 
                            className="type-display editable-field"
                            onClick={() => setEditingField({row: index, field: 'detected_type'})}
                            title="Click to change data type"
                          >
                            {(() => {
                              const type = mapping.detected_type || 'text';
                              const typeLabels: Record<string, string> = {
                                'monetary': 'Monetary Value',
                                'percentage': 'Percentage',
                                'date': 'Date',
                                'datetime': 'Date/Time',
                                'integer': 'Integer',
                                'decimal': 'Decimal',
                                'boolean': 'Boolean (Yes/No)',
                                'categorical': 'Category',
                                'identifier': 'ID/Code',
                                'numeric': 'Numeric',
                                'email': 'Email',
                                'phone': 'Phone',
                                'url': 'URL',
                                'json': 'JSON',
                                'mixed': 'Mixed',
                                'text': 'Text'
                              };
                              return typeLabels[type] || type;
                            })()}
                          </span>
                        )}
                      </div>
                      
                      {/* Data Format (Editable) */}
                      <div className="detail-item">
                        <label>Format Pattern:</label>
                        {editingField?.row === index && editingField?.field === 'data_format' ? (
                          <input
                            type="text"
                            defaultValue={mapping.data_format || ''}
                            placeholder="e.g., YYYY-MM-DD, $#,##0.00, ###-###-####"
                            onBlur={(e) => handleFieldEdit(index, 'data_format', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleFieldEdit(index, 'data_format', (e.target as HTMLInputElement).value);
                                setEditingField(null);
                              } else if (e.key === 'Escape') {
                                setEditingField(null);
                              }
                            }}
                            autoFocus
                            className="format-input"
                            title="Common formats: YYYY-MM-DD, MM/DD/YYYY, $#,##0.00, #.##%, (###) ###-####"
                          />
                        ) : (
                          <span 
                            className="format-display editable-field"
                            onClick={() => setEditingField({row: index, field: 'data_format'})}
                            title="Click to edit format pattern"
                          >
                            {mapping.data_format || 'Not identified'}
                          </span>
                        )}
                      </div>
                      
                      {/* Enrichment Confidence */}
                      <div className="detail-item">
                        <label>AI Confidence:</label>
                        <span className={`confidence-display confidence-${mapping.enrichment_confidence || 'medium'}`}>
                          {(mapping.enrichment_confidence || 'medium').toUpperCase()}
                        </span>
                      </div>
                      
                      {/* Validation Rules */}
                      <div className="detail-item full-width">
                        <label>Validation Rules:</label>
                        {mapping.validation_rules && mapping.validation_rules.length > 0 ? (
                          <div className="validation-rules">
                            {mapping.validation_rules.map((rule, i) => (
                              <span key={i} className="validation-rule" title="Automatically detected based on data analysis">
                                ✓ {rule}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="no-rules">No validation rules identified</span>
                        )}
                      </div>
                      
                      {/* Data Characteristics - Only show if has meaningful content */}
                      {mapping.data_characteristics && (
                        (mapping.data_characteristics.null_percentage !== undefined && mapping.data_characteristics.null_percentage > 0) ||
                        mapping.data_characteristics.unique_count !== undefined ||
                        mapping.data_characteristics.has_negatives ||
                        mapping.data_characteristics.detected_format ||
                        mapping.data_characteristics.value_range
                      ) && (
                        <div className="detail-item full-width">
                          <label>Data Characteristics:</label>
                          <div className="characteristics-grid">
                            {mapping.data_characteristics.null_percentage !== undefined && mapping.data_characteristics.null_percentage > 0 && (
                              <span className="char-item">
                                📊 {mapping.data_characteristics.null_percentage}% nulls
                              </span>
                            )}
                            {mapping.data_characteristics.unique_count !== undefined && (
                              <span className="char-item">
                                🔢 {mapping.data_characteristics.unique_count} unique values
                              </span>
                            )}
                            {mapping.data_characteristics.has_negatives && (
                              <span className="char-item">➖ Has negative values</span>
                            )}
                            {mapping.data_characteristics.detected_format && (
                              <span className="char-item">
                                📝 Format: {mapping.data_characteristics.detected_format}
                              </span>
                            )}
                            {mapping.data_characteristics.value_range && (
                              <span className="char-item">
                                📏 Range: {mapping.data_characteristics.value_range.min} to {mapping.data_characteristics.value_range.max}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Sample Values */}
                      {mapping.data_characteristics?.sample_values && 
                       mapping.data_characteristics.sample_values.length > 0 && (
                        <div className="detail-item full-width">
                          <label>Sample Values:</label>
                          <div className="sample-values">
                            {mapping.data_characteristics.sample_values.slice(0, 5).map((val, i) => (
                              <span key={i} className="sample-value">
                                {JSON.stringify(val)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Context Information */}
                      {(mapping.source_pattern || mapping.task_context) && (
                        <div className="detail-item full-width">
                          <label>Context:</label>
                          <div className="context-info">
                            {mapping.source_pattern && (
                              <span className="context-item">📁 Source: {mapping.source_pattern}</span>
                            )}
                            {mapping.task_context && (
                              <span className="context-item">🎯 Task: {mapping.task_context}</span>
                            )}
                            {mapping.last_used && (
                              <span className="context-item">
                                🕒 Last used: {new Date(mapping.last_used).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </React.Fragment>
                );
              })}
              </div>
            </div>
          </div>

          {showAllColumns && reviewData.skipped_columns_list && reviewData.skipped_columns_list.length > 0 && (
            <div className="all-columns-view">
              <h4>Columns Not Selected by Assertion</h4>
              <p className="skipped-columns-intro">
                These columns were skipped as they appear to be system-generated or not relevant for your task. 
                You can add any of these columns using the "+ Add More Columns" button above if needed.
              </p>
              <div className="skipped-columns-grid">
                {reviewData.skipped_columns_list.map((col: any, i: number) => {
                  const columnName = typeof col === 'string' ? col : col.column;
                  const reason = typeof col === 'object' ? col.reason_skipped : null;
                  return (
                    <div key={i} className="skipped-column-item">
                      <span className="column-name">{columnName}</span>
                      {reason && <span className="skip-reason">{reason}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="metadata-actions">
          <button 
            className="btn btn-outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Confirm & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};